import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { OrderTypes } from '@next/core-lib/cart';
import { AgrDataSource } from '@next/core-lib/table-v2';
import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable, ReplaySubject, Subject, take } from 'rxjs';
import { isItemTypeEligibleForCartType, skuIsOrderable } from 'src/app/product/helpers/add-to-cart-helpers';
import { selectProposalDeliveryMomentsAndZeroFlex, selectSilosWithProductListsLoaded } from 'src/app/product/store/selectors';

import { calculateDeliveryInformation } from 'src/app/utils/deliveryInformation-helpers';

import { LocationSiloWithProductList, SiloProductItemViewModel } from '../../models/silo-product-list.models';

export class SiloProductListDataSource extends AgrDataSource<SiloProductItemViewModel> {
  private readonly destroy$ = new Subject<void>();
  private readonly hasAtLeastAdvisorRole$ = this.store.select(AuthorizationSelectors.hasAtLeastAdvisorRole);
  private readonly isExactWeighingShipTo$ = this.store.select(UserSelectors.selectLocation).pipe(map((location) => location?.exactWeighing));
  private readonly cartType$ = new ReplaySubject<OrderTypes>(1);
  private readonly silo$ = new ReplaySubject<LocationSiloWithProductList>(1);

  constructor(private readonly store: Store) {
    super();
    this.store
      .select(selectSilosWithProductListsLoaded)
      .pipe(take(1))
      .subscribe((loaded) => this.loadingSubject.next(!loaded));
  }

  connect(): Observable<SiloProductItemViewModel[]> {
    return combineLatest([
      this.silo$,
      this.hasAtLeastAdvisorRole$,
      this.cartType$,
      this.store.select(selectProposalDeliveryMomentsAndZeroFlex),
      this.isExactWeighingShipTo$,
    ]).pipe(
      map(([silo, hasAtLeastAdvisorRole, cartType, proposedDeliveryDates, isExactWeighingShipTo]) => {
        const productList = [...(silo?.productList ?? [])];
        productList.sort((a, b) => new Date(b.lastDeliveryDate).getTime() - new Date(a.lastDeliveryDate).getTime());

        return productList.map(
          (product) =>
            ({
              ...product,
              isUserAllowedToOrderItem: skuIsOrderable(product, hasAtLeastAdvisorRole) && isItemTypeEligibleForCartType(product, cartType),
              deliveryInformation: calculateDeliveryInformation(proposedDeliveryDates, product.id, product.orderingInformation),
              isExactWeighingShipTo,
            }) satisfies SiloProductItemViewModel,
        );
      }),
    );
  }
  disconnect(): void {
    if (!this.destroy$.closed) {
      this.destroy$.next();
      this.destroy$.complete();
    }
  }

  setCartType(cartType: OrderTypes): void {
    this.cartType$.next(cartType);
  }

  setSilo(silo: LocationSiloWithProductList): void {
    this.silo$.next(silo);
  }
}
